const SCROLLABLE_OVERFLOW = ['auto', 'scroll', 'overlay'];

export interface ScrollGeometry {
  targetTop: number;
  targetHeight: number;
  scrollerTop: number;
  scrollerHeight: number;
  scrollTop: number;
  scrollHeight: number;
}

/**
 * The scrollTop that centres the target in its scroller, clamped to what the scroller can reach. Tops are
 * viewport coordinates (getBoundingClientRect), so the target's offset inside the content is its distance
 * from the scroller's top plus how far the scroller is already scrolled.
 */
export const centredScrollTop = ({ targetTop, targetHeight, scrollerTop, scrollerHeight, scrollTop, scrollHeight }: ScrollGeometry): number => {
  const offset = targetTop - scrollerTop + scrollTop;
  const centred = offset - (scrollerHeight - targetHeight) / 2;
  return Math.max(0, Math.min(centred, scrollHeight - scrollerHeight));
};

const findVerticalScroller = (el: Element): HTMLElement | null => {
  for (let parent = el.parentElement; parent; parent = parent.parentElement) {
    if (SCROLLABLE_OVERFLOW.includes(getComputedStyle(parent).overflowY) && parent.scrollHeight > parent.clientHeight)
      return parent;
  }
  return null;
};

const USER_INPUT_EVENTS = ['wheel', 'touchstart', 'pointerdown', 'keydown'];

/**
 * How long the element is kept centred while the page around it is still loading. Long enough for the ticket's
 * other sections to arrive on a slow tenant; any scroll, tap, click or key from the user ends it sooner.
 */
export const SETTLE_MS = 10000;

const centre = (el: Element): void => {
  const scroller = findVerticalScroller(el);
  if (!scroller) return;

  const target = el.getBoundingClientRect();
  const box = scroller.getBoundingClientRect();
  scroller.scrollTop = centredScrollTop({
    targetTop: target.top,
    targetHeight: target.height,
    scrollerTop: box.top,
    scrollerHeight: scroller.clientHeight,
    scrollTop: scroller.scrollTop,
    scrollHeight: scroller.scrollHeight
  });
};

/**
 * Centre an element in the nearest ancestor the user can scroll, and move nothing else.
 *
 * scrollIntoView and scrollIntoViewIfNeeded scroll every ancestor, including the `overflow: hidden` app
 * shell, which overflows the window slightly and so can be scrolled by script though never by the user. Opening
 * an approval from My Work did that: the header went off the top, a blank strip appeared at the bottom, and only
 * a reload brought the page back. #34148
 *
 * The card is ready before the rest of the ticket panel is: properties, forms and the other lists above it are
 * still loading and push it back out of view as they arrive. So it is centred again whenever an ancestor
 * resizes, until the user takes over or SETTLE_MS passes. Returns the function that stops that early.
 */
export const revealInScroller = (el: Element | null, win: Window = window): (() => void) => {
  if (!el) return () => undefined;
  centre(el);
  if (typeof ResizeObserver === 'undefined') return () => undefined;

  let frame = 0;
  const observer = new ResizeObserver(() => {
    if (frame) return;
    frame = win.requestAnimationFrame(() => {
      frame = 0;
      centre(el);
    });
  });
  for (let parent = el.parentElement; parent; parent = parent.parentElement) observer.observe(parent);

  const stop = () => {
    observer.disconnect();
    win.cancelAnimationFrame(frame);
    win.clearTimeout(timer);
    USER_INPUT_EVENTS.forEach(type => win.removeEventListener(type, stop, true));
  };
  const timer = win.setTimeout(stop, SETTLE_MS);
  USER_INPUT_EVENTS.forEach(type => win.addEventListener(type, stop, true));
  return stop;
};
